import * as SSACalcs from "app/calcs/SSA"
import { ActionCreator } from "app/redux/ActionCreator"
import {
    IRetirementPerson,
    isSamePersonForSSA,
    peopleAreSameForSSA,
} from "app/redux/Profile/model"
import { ActionTypes, ISSAOpAction, ISSAOpState } from "app/redux/ssa/reducer"
import {
    IPokeandPullRequest,
    IRequestedValues,
    IWorkbookValues,
    WorkbookServiceResponse,
} from "app/services/models/Workbook"
import { OperationStatus } from "app/services/OperationStatus"
import { SSAService } from "app/services/SSAService"
import { genericFailure } from "app/services/WebServiceBase"
import * as utils from "app/utils"
import { getWebServicesConfig } from "config"
import * as _ from "lodash"
import * as moment from "moment"

interface IWorkbookPayoutSchedule {
    when: moment.Moment
    memberAge: number
    spouseAge: number
    earliest: number
    latest: number
    selected: number
    full: number
    optimal: number
}

const debug = utils.makeDebug(false)
const chartDataRange = "n3:s63"
const fullRetirementAgeRange = "c11:d11"
const memberInputs = "c3:c8"
const optimizerInfoRange = "g15:g19"
const detailedMonthlyPayoutsRange = "a3:i1443"
const firstSerial = moment.utc("1/1/1900", "MM/DD/YYYY").subtract(1, "day")

function getStartingYear(cols: string, dob: moment.Moment): number {
    const tokens = cols[0].split("|")
    return dob.clone().add(tokens[0], "years").year()
}

function isUsefulData(row: any[]): boolean {
    return row[0].toLowerCase() !== "x|x"
}

function getFullRetirementAges(wr: WorkbookServiceResponse): moment.Duration[] {
    const fras = wr.Results.Inputs[fullRetirementAgeRange][0]
    return [
        moment.duration(fras[0], "years"),
        moment.duration(fras[1], "years"),
    ]
}

function getLifetimeBenefits(
    wr: WorkbookServiceResponse,
    member: IRetirementPerson
) {
    //const year = moment().year()
    const ltb: any[] = wr.Results.LifetimeBenefits[chartDataRange]
    const rows = _(ltb)
        .slice(1) // skip header
        .takeWhile(isUsefulData)
    const year = getStartingYear(rows.value()[0], member.dob)

    const earliest = rows
        .map((row: any[], index: number) => {
            return [year + index, row[1]]
        })
        .value()

    const latest = rows
        .map((row: any[], index: number) => {
            return [year + index, row[2]]
        })
        .value()

    const selected = rows
        .map((row: any[], index: number) => {
            return [year + index, row[3]]
        })
        .value()

    const full = rows
        .map((row: any[], index: number) => {
            return [year + index, row[4]]
        })
        .value()

    const optimal = rows
        .map((row: any[], index: number) => {
            return [year + index, row[5]]
        })
        .value()

    return { earliest, latest, full, selected, optimal }
}

function getMonthlyBenefits(
    wr: WorkbookServiceResponse,
    member: IRetirementPerson
) {
    //const year = moment().year()
    const ltb: any[] = wr.Results.LifetimeBenefits[chartDataRange]
    const rows = _(ltb)
        .slice(1) // skip header
        .takeWhile(isUsefulData)
    const year = getStartingYear(rows.value()[0], member.dob)

    const dx = 5
    const prevAmount = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]

    const a0 = rows.value()[0]
    const a1 = a0[0]
    const a2 = a0[1]
    const a3 = a0[2]

    // the spreadsheet only returns 6 columns per row. We need monthly amounts for each of the 5 categories
    // so, we'll expand each row as needed
    rows.forEach((r: any[]) => {
        for (let i = r.length; i < 6 + 5; i++) {
            r.push(0)
        }

        // caclulate the monthly data (this ought to be in the spreadsheet!)
        //
        // when i =
        //  0 - not used (it's the age column)
        //  1 - earliest
        //  2 - latest
        //  3 - selected
        //  4 - full
        //  5 - optimal
        //  6 - earliest (monthly)
        //  7 - latest (monthly)
        //  8 - selected (monthly)
        //  9 - full (monthly)
        //  10 - optimal (monthly)
        for (let i = 1; i <= 5; i++) {
            const deltaThisYear = r[i] - prevAmount[i]
            prevAmount[i] = r[i]
            r[dx + i] = deltaThisYear / 12
        }
    })

    const earliest = rows
        .map((row: any[], index: number) => {
            return [year + index, row[dx + 1]]
        })
        .value()

    const latest = rows
        .map((row: any[], index: number) => {
            return [year + index, row[dx + 2]]
        })
        .value()

    const selected = rows
        .map((row: any[], index: number) => {
            return [year + index, row[dx + 3]]
        })
        .value()

    const full = rows
        .map((row: any[], index: number) => {
            return [year + index, row[dx + 4]]
        })
        .value()

    const optimal = rows
        .map((row: any[], index: number) => {
            return [year + index, row[dx + 5]]
        })
        .value()

    return { earliest, latest, full, selected, optimal }
}

function getOptimizerInfo(wr: WorkbookServiceResponse): any[] {
    const r = _.flattenDeep(wr.Results.Inputs[optimizerInfoRange])
    r[0] = moment.duration(r[0], "years")
    r[1] = moment.duration(r[1], "years")
    return r
}

class SSAActionCreator extends ActionCreator<ISSAOpState, ISSAOpAction> {
    protected monthlyPayoutSchedule: IWorkbookPayoutSchedule[]

    protected endpoint = getWebServicesConfig().ssaWorkbookEndpoint
    protected ssaService = new SSAService(this.endpoint)

    issueRequest(
        people: IRetirementPerson[]
    ): Promise<WorkbookServiceResponse> {
        const member = people[0]
        let spousalInputs: any = [[""], [""], [""], [""], [""], [""]]
        let singleOrMarried = 1
        if (people.length > 1) {
            singleOrMarried = 2
            const spouse = people[1]
            spousalInputs = [
                [spouse.dob.format("MM/DD/YY")],
                [spouse.PIA],
                [spouse.WEPReduction || 0],
                [spouse.govPensionForGPO || 0],
                [spouse.selectedAgeExpectancy],
                [spouse.selectedRetirementAge],
            ]
        }
        const inputs: IWorkbookValues = {
            Inputs: {
                "d1:d1": [[singleOrMarried]],
                [memberInputs]: [
                    [member.dob.format("MM/DD/YY")],
                    [member.PIA],
                    [member.WEPReduction || 0],
                    [member.govPensionForGPO || 0],
                    [member.selectedAgeExpectancy],
                    [member.selectedRetirementAge],
                ],
                "d3:d8": spousalInputs,
            },
        }
        // tslint:disable-next-line:no-console
        console.log("SSA.issueRequest- member: " + JSON.stringify(member))

        const desiredCells: IRequestedValues = {
            Inputs: [
                fullRetirementAgeRange,
                optimizerInfoRange,
                "b13:g19",
                memberInputs,
                "d2:d8",
            ],
            LifetimeBenefits: [chartDataRange, detailedMonthlyPayoutsRange],
        }
        const req: IPokeandPullRequest = {
            inputs,
            outputs: desiredCells,
        }
        return this.ssaService.pokeAndPull(
            this.getSharedVar("accessToken"),
            req
        )
    }

    getMonthlyPayoutSchedule(
        wr: WorkbookServiceResponse
    ): IWorkbookPayoutSchedule[] {
        if (!wr.Results.Inputs || !wr.Results.Inputs[memberInputs]) {
            debugger
        }
        // tslint:disable-next-line:no-console
        console.log(
            "Post-processing SSA from inputs: " +
                JSON.stringify(wr.Results.Inputs[memberInputs])
        )
        // convert all the Excel 'serial dates' into moments
        // and the inner arrays to a proper data structure
        return _(wr.Results.LifetimeBenefits[detailedMonthlyPayoutsRange])
            .drop(1)
            .map((row) => ({
                when: firstSerial.clone().add(row[0], "days"),
                memberAge: row[1],
                spouseAge: row[2],
                earliest: row[4],
                latest: row[5],
                selected: row[6],
                full: row[7],
                optimal: row[8],
            }))
            .value()
    }

    /**
     * Run the SSA Optimizer workbook.
     *
     * @param people The (2) people to run the calculations for
     *
     * @returns a WorkbookServiceResponse. (TODO: need a better return!)
     */
    runOptimizer(people: IRetirementPerson[]) {
        if (!SSACalcs.haveEnoughDataForSSACalculation(people)) {
            throw new Error(
                "runOptimizer called with people NOT ready to perform the calculation!"
            )
        }

        this.dispatch(this.ssaOpStarting(people))
        const startTime = moment.utc()

        return this.issueRequest(people)
            .then((wr: WorkbookServiceResponse) => {
                const endTime = moment.utc()
                const elapsed = endTime.diff(startTime)
                debug("It took " + elapsed + "ms for calculation")
                let needPostProcess = false
                if (wr.IsError) {
                    this.dispatch(this.ssaOpFailed(wr))
                } else {
                    if (!wr.Results) {
                        this.dispatch(this.ssaOpFailed(wr))
                    } else {
                        needPostProcess = true
                        this.dispatch(this.ssaOpCompleted(wr))
                    }
                }
                //return needPostProcess ? this.getMonthlyPayoutSchedule(wr) : []
                return {
                    rawMonthlyPaymentSchedule:
                        this.getMonthlyPayoutSchedule(wr),
                    fullRetirementAges: getFullRetirementAges(wr),
                    info: getOptimizerInfo(wr),
                    getLifetimeBenefits: (person: IRetirementPerson) =>
                        getLifetimeBenefits(wr, person),
                    getMonthlyBenefits: (person: IRetirementPerson) =>
                        getMonthlyBenefits(wr, person),
                }
            })
            .catch((reason) => {
                this.dispatch(this.ssaOpFailed(reason))
                return Promise.reject(reason)
            })
    }

    private ssaOpStarting = (people: IRetirementPerson[]): ISSAOpAction => {
        return {
            type: ActionTypes.SSA_OP_STARTING,
            ssa: {
                op: new OperationStatus().setStarting(
                    "starting SSA calculations"
                ),
                results: undefined,
            },
        }
    }

    private ssaOpCompleted = (
        response: WorkbookServiceResponse
    ): ISSAOpAction => {
        return {
            type: ActionTypes.SSA_OP_COMPLETED,
            ssa: {
                op: OperationStatus.fromResponse(response),
                results: response.Results,
            },
        }
    }

    private ssaOpFailed(reason: any): ISSAOpAction {
        return {
            type: ActionTypes.SSA_OP_FAILED,
            ssa: { op: genericFailure(reason), results: undefined },
        }
    }
}

const SSAActions = new SSAActionCreator()

export {
    SSAActions,
    SSAActionCreator,
    WorkbookServiceResponse,
    IWorkbookPayoutSchedule,
}



// WEBPACK FOOTER //
// ./src/app/redux/SSA/actions/index.ts